/**
 * Edit image block.
 * @module components/manage/Blocks/Image/Edit
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import ImageSidebar from '@plone/volto/components/manage/Blocks/Image/ImageSidebar';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';

import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import config from '@plone/volto/registry';
import { getContent } from '@plone/volto/actions/content/content';
import ContentImage from 'volto-uni-bonn/components/Image/ContentImage';
import Copyright from 'volto-uni-bonn/components/Image/Copyright';

import { ImageInput } from '@plone/volto/components/manage/Widgets/ImageWidget';

function Edit(props) {
  const { data } = props;
  const dispatch = useDispatch();

  const handleChange = React.useCallback(
    async (id, image, { title, image_field, image_scales } = {}) => {
      const url = Array.isArray(image)
        ? image?.[0]?.['@id']
        : image
          ? image['@id'] || image
          : '';

      const asset =
        url && isInternalURL(url)
          ? await dispatch(
              getContent(flattenToAppURL(url), null, `${props.block}-image`),
            ).catch(() => null)
          : null;

      props.onChangeBlock(props.block, {
        ...props.data,
        url: flattenToAppURL(url),
        image_field,
        image_scales,
        alt: props.data.alt || title || '',
        copyright: asset?.rights || '',
      });
    },
    [props, dispatch],
  );

  return (
    <>
      <figure
        className={cx(
          'block image align',
          {
            center: !Boolean(data.align),
          },
          data.align,
        )}
      >
        {data.url ? (
          <ContentImage
            className={cx({
              'full-width': data.align === 'full',
              large: data.size === 'l',
              medium: data.size === 'm',
              small: data.size === 's',
            })}
            item={
              data.image_scales
                ? {
                    '@id': data.url,
                    image_field: data.image_field,
                    image_scales: data.image_scales,
                  }
                : undefined
            }
            src={
              data.image_scales
                ? undefined
                : isInternalURL(data.url)
                  ? // Backwards compat in the case that the block is storing the full server URL
                    (() => {
                      if (data.size === 'l')
                        return `${flattenToAppURL(data.url)}/@@images/image`;
                      if (data.size === 'm')
                        return `${flattenToAppURL(
                          data.url,
                        )}/@@images/image/preview`;
                      if (data.size === 's')
                        return `${flattenToAppURL(data.url)}/@@images/image/mini`;
                      return `${flattenToAppURL(data.url)}/@@images/image`;
                    })()
                  : data.url
            }
            sizes={config.blocks.blocksConfig.image.getSizes(data)}
            alt={data.alt || ''}
            loading="lazy"
            responsive={true}
          >
            <Copyright>{data.copyright}</Copyright>
          </ContentImage>
        ) : (
          <>
            <ContentImage />
            {props.selected && (
              <ImageInput
                onChange={handleChange}
                placeholderLinkInput={data.placeholder}
                block={props.block}
                id={props.block}
                objectBrowserPickerType={'image'}
              />
            )}
          </>
        )}
        <SidebarPortal selected={props.selected}>
          <ImageSidebar {...props} />
        </SidebarPortal>
      </figure>
    </>
  );
}

export default withBlockExtensions(Edit);
